import type {CLDVideoAsset, PlayerConf, Transformation} from '_types'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

/**
 * Responsive video delivery for Cloudinary — parametric, SSR-safe, CDN/cache-agnostic.
 *
 * Design (see specs/cloudinary-video-integration.md §8):
 * - The HTML is device-agnostic (served identically to all devices behind Akamai/Cloudflare).
 *   We therefore emit EVERY variant as a <source> and let the browser pick+download only the
 *   best match. No SSR device detection, no hydration rewrite, no double download.
 * - Density is expressed via media queries `(min-resolution: Xdppx)`, not dpr_auto (unsupported
 *   on native <video>) and not runtime devicePixelRatio (not available at SSR / behind CDN).
 * - Format/codec are explicit per <source> (no f_auto): full control, avoids the player-injected
 *   `f_auto:video` → WebM/VP9 blur seen on SFRA.
 * - URLs are rebuilt from the asset's public_id/version (not by mangling secure_url), so swapping
 *   the delivery format is clean and extension-safe.
 *
 * Public API: a single entry point, `buildVideoSources`, plus the `VIDEO_SLOTS` catalog.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A generated <source> entry for the native <video> tag. */
export type VideoSource = {
    /** Media query, e.g. '(min-width: 64rem) and (min-resolution: 2dppx)'. Omitted = base. */
    media?: string
    /** Full MIME incl. codecs, e.g. 'video/mp4; codecs="hvc1"'. */
    type: string
    /** Cloudinary delivery URL. */
    src: string
}

/** Explicit delivery format/codec pair (verified against Cloudinary). */
type VideoFormat = {
    container: 'webm' | 'mp4'
    codec: 'av1' | 'vp9' | 'h265' | 'h264'
    mime: string
    /**
     * Per-codec quality override. Cloudinary normalizes `q_` differently per codec —
     * AV1/HEVC are so efficient that `q_auto:best` or `q_90` produce bitrates 10-20× lower
     * than H.264 at the same q_ value, resulting in visible quality loss on premium content.
     * When set, this overrides the global quality from preferences for this format.
     * See specs/cloudinary-video-integration.md §3.5 and quality calibration data.
     */
    quality?: string
}

/** Per-breakpoint physical widths, one per target density (CSS width × DPR). */
type SlotBreakpoint = {
    /** Breakpoint min-width in px. Omitted = base (mobile, no min-width query). */
    minWidth?: number
    /** Physical px to serve at DPR1 / DPR2 / DPR3 / DPR4. Omit a density to skip it. */
    dpr1: number
    dpr2?: number
    dpr3?: number
    dpr4?: number
}

/** A slot: breakpoints ordered from the largest to the base. */
export type VideoSlot = SlotBreakpoint[]

// ---------------------------------------------------------------------------
// Format catalog (verified on Cloudinary: f_<container>,vc_<codec>)
// Ordered most-efficient → universal fallback. The browser picks the first it can decode.
// Safari needs the explicit `codecs="hvc1"` to select HEVC.
// ---------------------------------------------------------------------------

// Quality calibrated on a proper high-res master (3840×2160, 12 MB H.264 source).
// q_100 effectively DISABLES compression (output ≥ master); q_80 is the sweet spot where
// every codec compresses meaningfully while staying visually sharp on premium content.
// Measured @ w_3840: VP9 q_80 ≈ 6.5 MB, HEVC q_80 ≈ 8 MB (vs 12 MB master).
// Note: HEVC/Safari barely compresses above q_80 (q_90 ≈ master size) — keep HEVC at 80.
//
// COST REDUCTION: AV1 removed. AV1 bills at Cloudinary's highest rate (≈8× a standard
// transformation) while VP9 covers the exact same Chromium/Firefox browser set at q_80 with
// only a ~+4% size delta and no visible quality difference on premium content. Dropping AV1 is
// the single biggest lever on video transformation cost. Order stays efficient→universal:
// VP9 (webm) → HEVC/H.265 (mp4, Safari via codecs="hvc1") → H.264 (mp4, universal fallback).
export const DEFAULT_VIDEO_FORMATS: VideoFormat[] = [
    {container: 'webm', codec: 'vp9', mime: 'video/webm; codecs="vp9"', quality: '80'},
    {container: 'mp4', codec: 'h265', mime: 'video/mp4; codecs="hvc1"', quality: '80'},
    {container: 'mp4', codec: 'h264', mime: 'video/mp4', quality: '80'},
]

// ---------------------------------------------------------------------------
// Slot catalog — centralized. Future device-resolution bumps are a data change here.
// Theme breakpoints (px): sm 480, md 768, lg 1024, xl 1200, 2xl 1536.
// Widths are PHYSICAL px (CSS width × DPR), capped at delivery by c_limit (no upscaling past master).
// ---------------------------------------------------------------------------

/**
 * 01_FULL hero — full-bleed (home/editorial). 100vw at every breakpoint.
 * Two assets only: this DESKTOP asset (16:9, also covers tablet — no dedicated tablet asset)
 * and the MOBILE asset below. Opzione C: distinct desktop widths at lg and 2xl.
 * Master consigliato: 3840×2160 (see specs/content-production-sizes.md). c_limit caps to it.
 */
const SLOT_FULL_DESKTOP: VideoSlot = [
    // 2xl ≥1536: CSS ~2560. 2x capped to the 4K master via c_limit.
    {minWidth: 1536, dpr1: 2560, dpr2: 3840},
    // lg/xl ≥1024: CSS ~1920. Tablet (768–1023) now falls through to this band:
    // there is NO tablet-specific master and tablet traffic is very low; the desktop
    // DPR1 width (1920) is ≥ the old tablet box (1024), so tablet is served a larger
    // (never smaller) width — no blur — while the md density entry is saved.
    {minWidth: 1024, dpr1: 1920, dpr2: 3840},
    // base (fallback only when no mobile asset): CSS ~440. Mobile capped at DPR3.
    {dpr1: 440, dpr2: 880, dpr3: 1320},
]

/**
 * 01_FULL hero — dedicated MOBILE (portrait, 5:8) asset. Serves the base band only.
 * Master consigliato: 1760×2816. Mobile capped at DPR3 (~1320 physical px @ 440 CSS):
 * the DPR4 band (Android flagship) is dropped as a cost lever — DPR3 is already sharp on
 * mobile and the 4× sources are the heaviest per slot. c_limit still caps to the master.
 */
const SLOT_FULL_MOBILE: VideoSlot = [{dpr1: 440, dpr2: 880, dpr3: 1320}]

/**
 * 01_FULL hero in HALF-PAGE (listing banner_half) — DESKTOP asset.
 * Container ~50vw (flex 1 0 0 alongside the products grid). Portrait aspect (~4:5).
 * Two assets: desktop (iframe_id, portrait) + optional mobile (iframem_id, portrait).
 * Master consigliato: 2560×3200.
 */
const SLOT_FULL_HALF_DESKTOP: VideoSlot = [
    // 2xl ≥1536: ~50vw ≈1280 CSS.
    {minWidth: 1536, dpr1: 1280, dpr2: 2560},
    // lg/xl ≥1024: ~50vw ≈960 CSS. Tablet (768–1023) collapses into this band
    // (no tablet master, low tablet traffic): desktop DPR1 (960) ≥ old tablet box (512) → no blur.
    {minWidth: 1024, dpr1: 960, dpr2: 1920},
    // base (fallback when no mobile asset): full-width mobile ≈440 CSS. Mobile capped at DPR3.
    {dpr1: 440, dpr2: 880, dpr3: 1320},
]

/**
 * 01_FULL hero in HALF-PAGE — MOBILE asset. In half-page mobile is full-width (column layout).
 * Same as SLOT_FULL_MOBILE — reuses the same dimensions.
 */
const SLOT_FULL_HALF_MOBILE: VideoSlot = [{dpr1: 440, dpr2: 880, dpr3: 1320}]

/**
 * 06C DOUBLE SAME — each column is 6/12 (≈50vw) on tablet+desktop.
 * Mobile: 12/12 full-width (100vw) by default, OR 6/12 (≈50vw) when mobile_side_by_side.
 * Ratio is fixed (equal column heights) — handled by CSS/production, slot only sets widths.
 * Two variants so we never oversize the side-by-side case nor undersize the stacked case.
 */
// Desktop capped at DPR2 (DPR3 on desktop is an unrealistic edge case); mobile capped at DPR3.
// Tablet (md) collapses into the desktop band: desktop DPR1 (1280) ≥ old tablet box (512) → no blur.
const SLOT_DOUBLE_SAME: VideoSlot = [
    {minWidth: 1024, dpr1: 1280, dpr2: 2560}, // lg/xl/2xl + tablet: 50vw (≈1280 CSS @2560vp)
    {dpr1: 440, dpr2: 880, dpr3: 1320}, // base: 100vw stacked, mobile capped DPR3
]
const SLOT_DOUBLE_SAME_SIDE_BY_SIDE: VideoSlot = [
    {minWidth: 1024, dpr1: 1280, dpr2: 2560},
    {dpr1: 220, dpr2: 440, dpr3: 660}, // base: 50vw side-by-side (≈220 CSS), mobile capped DPR3
]

/**
 * 25C TWO THIRDS ONE THIRD — desktop: left col 8/12 (≈66vw), right col 4/12 (≈33vw).
 * Mobile: both 12/12 full-width. Ratio fixed. Separate slots per column to size precisely.
 */
// Tablet (md) collapses into the desktop band (desktop DPR1 ≥ old tablet box → no blur); mobile capped DPR3.
const SLOT_TWO_THIRD_LARGE: VideoSlot = [
    {minWidth: 1024, dpr1: 1707, dpr2: 3414}, // 2/3 col + tablet: 66vw (≈1707 CSS @2560vp); DPR1 1707 ≥ tablet 683
    {dpr1: 440, dpr2: 880, dpr3: 1320}, // base: 100vw, mobile capped DPR3
]
const SLOT_TWO_THIRD_SMALL: VideoSlot = [
    {minWidth: 1024, dpr1: 853, dpr2: 1707}, // 1/3 col + tablet: 33vw (≈853 CSS @2560vp); DPR1 853 ≥ tablet 341
    {dpr1: 440, dpr2: 880, dpr3: 1320}, // base: 100vw, mobile capped DPR3
]

/**
 * 09C/09AC HALF TEXT HALF PHOTO — media half is 6/12 (≈50vw) desktop/tablet, 100vw mobile.
 * No side-by-side variant. Same widths as doubleSame (named separately for clarity).
 */
const SLOT_HALF_TEXT_HALF_PHOTO: VideoSlot = [
    // 50vw desktop + tablet (md collapsed): desktop DPR1 (1280) ≥ old tablet box (512) → no blur.
    {minWidth: 1024, dpr1: 1280, dpr2: 2560},
    {dpr1: 440, dpr2: 880, dpr3: 1320}, // 100vw mobile, capped DPR3
]

/**
 * 29 STICKY BANNER — small fixed bottom banner. image width ≈33vw−120px (xl), 35% (md), 45% (base).
 * Desktop ≈733 CSS @2560vp; mobile ≈45vw. Desktop capped DPR2, mobile DPR4.
 */
const SLOT_STICKY_BANNER: VideoSlot = [
    // ≈33vw−120 desktop + tablet (md collapsed): desktop DPR1 (733) ≥ old tablet box (360) → no blur.
    {minWidth: 1024, dpr1: 733, dpr2: 1466},
    {dpr1: 198, dpr2: 396, dpr3: 594}, // 45vw mobile (≈198 CSS @440), capped DPR3
]

/**
 * 07C WALL MIXED — grid with N columns = items per row. Video cell width = viewport / N.
 * Two common cases: 3-col row (≈33vw cell) and 4-col row (≈25vw cell). Separate slots.
 * Desktop capped DPR2, mobile DPR4. Mobile is the single-column proximity per the grid.
 */
// Tablet (md) collapses into the desktop band (desktop DPR1 ≥ old tablet box → no blur); mobile capped DPR3.
const SLOT_WALL_MIXED_3COL: VideoSlot = [
    {minWidth: 1024, dpr1: 853, dpr2: 1706}, // 33vw desktop + tablet (≈853 CSS @2560); DPR1 853 ≥ tablet 341
    {dpr1: 440, dpr2: 880, dpr3: 1320}, // mobile, capped DPR3
]
const SLOT_WALL_MIXED_4COL: VideoSlot = [
    {minWidth: 1024, dpr1: 640, dpr2: 1280}, // 25vw desktop + tablet (≈640 CSS @2560); DPR1 640 ≥ tablet 256
    {dpr1: 440, dpr2: 880, dpr3: 1320}, // mobile, capped DPR3
]

/**
 * 40 EDITORIAL SLIDER — media box width {base:100vw, md:75vw, lg:50vw}. Dedicated desktop+mobile assets.
 * No PDF reference: sized from CSS. Desktop capped DPR2, mobile DPR4.
 */
const SLOT_EDITORIAL_SLIDER: VideoSlot = [
    // 50vw desktop + tablet (md collapsed): the tablet box is 75vw (≈1152 CSS) and the desktop
    // DPR1 is 1280 ≥ 1152, so tablet is served a larger-or-equal width → no blur.
    {minWidth: 1024, dpr1: 1280, dpr2: 2560},
    {dpr1: 440, dpr2: 880, dpr3: 1320}, // 100vw mobile, capped DPR3
]

/** Public catalog of named slots. Add new slots here as modules are migrated. */
export const VIDEO_SLOTS = {
    fullDesktop: SLOT_FULL_DESKTOP,
    fullMobile: SLOT_FULL_MOBILE,
    fullHalfDesktop: SLOT_FULL_HALF_DESKTOP,
    fullHalfMobile: SLOT_FULL_HALF_MOBILE,
    doubleSame: SLOT_DOUBLE_SAME,
    doubleSameSideBySide: SLOT_DOUBLE_SAME_SIDE_BY_SIDE,
    twoThirdLarge: SLOT_TWO_THIRD_LARGE,
    twoThirdSmall: SLOT_TWO_THIRD_SMALL,
    halfTextHalfPhoto: SLOT_HALF_TEXT_HALF_PHOTO,
    stickyBanner: SLOT_STICKY_BANNER,
    wallMixed3Col: SLOT_WALL_MIXED_3COL,
    wallMixed4Col: SLOT_WALL_MIXED_4COL,
    editorialSlider: SLOT_EDITORIAL_SLIDER,
} as const

export type VideoSlotName = keyof typeof VIDEO_SLOTS

// ---------------------------------------------------------------------------
// Builder
// ---------------------------------------------------------------------------

type BuildVideoSourcesOptions = {
    /** Named slot or an explicit slot definition. */
    slot: VideoSlotName | VideoSlot
    /** Preference-derived transformation (quality, bit_rate). */
    transformation: Transformation
    /** Optional CNAME replacing res.cloudinary.com. */
    cname?: string
    /** Explicit formats, efficient→fallback. Defaults to AV1→VP9→HEVC→H264. */
    formats?: VideoFormat[]
}

const DPPX_BY_DENSITY = {dpr1: 1, dpr2: 2, dpr3: 3, dpr4: 4} as const
const DENSITY_ORDER: Array<keyof typeof DPPX_BY_DENSITY> = ['dpr4', 'dpr3', 'dpr2', 'dpr1']

/**
 * Builds the editorial video analytics label (SFRA `data-analyticstitle` parity):
 * "Video editoriale {position} numero {index}". Pass react-intl's formatMessage.
 * Fed to the atom `Video` `analyticsTitle` prop by editorial modules with controls=true.
 */
export const buildEditorialVideoAnalyticsTitle = (
    formatMessage: (descriptor: {id: string; defaultMessage: string}, values?: Record<string, string | number>) => string,
    position: number | string = 1,
    index: number | string = 1,
): string =>
    formatMessage({id: 'analytics.title.videos.editoriale', defaultMessage: 'Editorial video {position} episode {index}'}, {position, index})

// Build the media query for a breakpoint+density. Highest density first so the browser
// matches the sharpest source whose `min-resolution` its DPR satisfies.
const mediaQuery = (minWidth: number | undefined, density: keyof typeof DPPX_BY_DENSITY): string | undefined => {
    const parts: string[] = []
    if (minWidth) parts.push(`(min-width: ${minWidth / 16}rem)`)
    const dppx = DPPX_BY_DENSITY[density]
    if (dppx > 1) parts.push(`(min-resolution: ${dppx}dppx)`)
    return parts.length ? parts.join(' and ') : undefined
}

/**
 * Resolve the Cloudinary delivery base (up to and incl. `video/upload`), the version segment
 * and the extension-less public id from the asset. Prefers structured fields (public_id, version,
 * cname) and falls back to parsing secure_url only if needed.
 */
const resolveAssetParts = (
    playerConf: PlayerConf,
    cname?: string,
): {base: string; version: string; publicId: string} | null => {
    const asset = playerConf?.sourceConfig?.video?.asset
    if (!asset) return null

    const host = cname && cname !== 'res.cloudinary.com' ? cname : playerConf.cname || 'res.cloudinary.com'
    const version = asset.version ? `v${asset.version}` : ''

    // Preferred path: build from public_id (no extension, no transformations).
    if (asset.public_id) {
        return {base: `https://${host}/video/upload`, version, publicId: asset.public_id}
    }

    // Fallback: derive from secure_url (strip transformations + version + extension).
    const secure = asset.secure_url
    if (!secure) return null
    const normalized = cname && cname !== 'res.cloudinary.com' ? secure.replace('res.cloudinary.com', cname) : secure
    const afterUpload = normalized.split('video/upload/')[1]
    if (!afterUpload) return null
    const noVersion = afterUpload.replace(/^v\d+\//, '')
    const publicId = noVersion.replace(/\.[a-z0-9]+(\?.*)?$/i, '')
    const base = normalized.split('video/upload/')[0] + 'video/upload'
    return {base, version, publicId}
}

/**
 * @name buildVideoSources
 *
 * @description
 * Build the ordered list of <source> entries (breakpoint × density × format) for a Cloudinary
 * video asset. Device/density selection is delegated entirely to the browser via media queries;
 * the markup is identical for all devices (CDN/cache-safe) and SSR-safe (pure, no window).
 *
 * @returns VideoSource[] ordered: largest breakpoint → highest density → most efficient format.
 */
export const buildVideoSources = (video: CLDVideoAsset | undefined, options: BuildVideoSourcesOptions): VideoSource[] => {
    if (!video?.playerConf) return []

    const {slot, transformation, cname, formats = DEFAULT_VIDEO_FORMATS} = options
    const slotDef = typeof slot === 'string' ? VIDEO_SLOTS[slot] : slot
    if (!slotDef?.length) return []

    let playerConf: PlayerConf
    try {
        playerConf = JSON.parse(video.playerConf)
    } catch (e) {
        logger.error('buildVideoSources: invalid playerConf', {additionalProperties: {error: String(e)}})
        return []
    }

    const parts = resolveAssetParts(playerConf, cname)
    if (!parts) return []
    const {base, version, publicId} = parts

    // Shared delivery transformations from preferences. Quality is now per-format (see VideoFormat.quality);
    // bit_rate is still global (from preferences). Both are applied in the inner format loop.
    const {bit_rate, quality} = transformation ?? {}

    // Raw transformations configured on the asset in Page Designer. These may include:
    // - delivery params we already generate per slot (w_, h_, c_, dpr_, f_, q_, vc_, br_) → DROPPED
    //   to avoid duplicates/conflicts with our responsive combos;
    // - author-intent params we must PRESERVE: overlays (l_), effects (e_), gravity (g_),
    //   borders, badges/logos, named transformations (t_), etc.
    // Prefixes we own and therefore strip from the raw segment:
    const OWNED_PREFIX = /^(w|h|c|dpr|f|q|vc|br)_/
    const rawTransformations = (playerConf.sourceConfig?.transformation ?? [])
        .map((t) => t.raw_transformation)
        .filter(Boolean)
        .flatMap((raw) => (raw as string).split('/')) // a raw may chain multiple components with "/"
        .map((comp) =>
            comp
                .split(',')
                .filter((p) => !OWNED_PREFIX.test(p.trim())) // drop owned delivery params
                .join(','),
        )
        .filter((comp) => comp.length > 0) as string[]

    const versionSeg = version ? `${version}/` : ''
    const sources: VideoSource[] = []

    for (const bp of slotDef) {
        for (const density of DENSITY_ORDER) {
            const width = bp[density]
            if (!width) continue
            const media = mediaQuery(bp.minWidth, density)
            for (const fmt of formats) {
                const sizing = `w_${width},c_limit`
                const fmtPart = `f_${fmt.container},vc_${fmt.codec}`
                // Per-codec quality override takes precedence over the global preference.
                const effectiveQuality = fmt.quality || (quality && quality !== 'none' ? quality : null)
                const qParts = [...(bit_rate && bit_rate !== 'none' && bit_rate !== 'default' ? [`br_${bit_rate}`] : []), ...(effectiveQuality ? [`q_${effectiveQuality}`] : [])]
                const comp = [sizing, ...qParts, fmtPart].join(',')
                const rawSeg = rawTransformations.length ? `${rawTransformations.join('/')}/` : ''
                const src = `${base}/${comp}/${rawSeg}${versionSeg}${publicId}.${fmt.container}`
                sources.push({media, type: fmt.mime, src})
            }
        }
    }

    return sources
}
